import { create } from 'zustand';
import { RECIPES } from '@/data/recipes';
import { TIPS } from '@/data/tips';
import { storageClearApollo, storageGet, storageSet, KEYS } from '@/lib/storage';
import { todayKey } from '@/lib/format';
import type { WorkoutSummary } from '@/lib/stats';
import type { Measurement, MotivTrack, Profile, Recipe, ShoppingList, TodayLog } from '@/types';

interface AppState {
  hydrated: boolean;
  profile: Profile | null;
  todayKey: string;
  todayLog: TodayLog;
  measurements: Measurement[];
  recipes: Recipe[];
  shopping: ShoppingList | null;
  motivTracks: MotivTrack[];
  workoutHistory: WorkoutSummary[];
  tip: string;
}

interface AppActions {
  hydrate: () => Promise<void>;
  setProfile: (p: Profile | null) => Promise<void>;
  setTodayLog: (tl: TodayLog) => Promise<void>;
  patchTodayLog: (patch: Partial<TodayLog> | ((cur: TodayLog) => TodayLog)) => Promise<void>;
  setMeasurements: (m: Measurement[]) => Promise<void>;
  setRecipes: (r: Recipe[]) => Promise<void>;
  setShopping: (s: ShoppingList | null) => Promise<void>;
  setMotivTracks: (m: MotivTrack[]) => Promise<void>;
  pushWorkoutHistory: (s: WorkoutSummary) => Promise<void>;
  setTip: (t: string, persist?: boolean) => Promise<void>;
  rolloverDay: (newKey: string) => Promise<void>;
  reset: () => Promise<void>;
}

const initial: AppState = {
  hydrated: false,
  profile: null,
  todayKey: todayKey(),
  todayLog: {},
  measurements: [],
  recipes: RECIPES,
  shopping: null,
  motivTracks: [],
  workoutHistory: [],
  tip: '',
};

const pickRandomTip = (): string => TIPS[Math.floor(Math.random() * TIPS.length)] ?? '';

export const useStore = create<AppState & AppActions>((set, get) => ({
  ...initial,

  hydrate: async () => {
    const tk = todayKey();
    const rawProfile = await storageGet<Profile | null>(KEYS.profile, null);

    // Migration : ancien profil sans level → fallback intermediate
    let profile = rawProfile;
    if (profile && !profile.level) {
      profile = { ...profile, level: 'intermediate' };
      await storageSet(KEYS.profile, profile);
    }

    if (!profile) {
      set({ hydrated: true, todayKey: tk });
      return;
    }

    const [measurements, recipes, shopping, motivTracks, workoutHistory, todayLog, savedTip] = await Promise.all([
      storageGet<Measurement[]>(KEYS.measurements, []),
      storageGet<Recipe[]>(KEYS.recipes, RECIPES),
      storageGet<ShoppingList | null>(KEYS.shopping, null),
      storageGet<MotivTrack[]>(KEYS.motivTracks, []),
      storageGet<WorkoutSummary[]>(KEYS.workoutHistory, []),
      storageGet<TodayLog>(`${KEYS.dayPrefix}${tk}`, {}),
      storageGet<string | null>(`${KEYS.tipPrefix}${tk}`, null),
    ]);

    let tip = savedTip ?? '';
    if (!tip) {
      tip = pickRandomTip();
      void storageSet(`${KEYS.tipPrefix}${tk}`, tip);
    }

    set({
      hydrated: true,
      profile,
      todayKey: tk,
      todayLog,
      measurements,
      recipes,
      shopping,
      motivTracks,
      workoutHistory,
      tip,
    });
  },

  setProfile: async (p) => {
    set({ profile: p });
    if (p) await storageSet(KEYS.profile, p);
    else await storageSet(KEYS.profile, null);
  },

  setTodayLog: async (tl) => {
    set({ todayLog: tl });
    await storageSet(`${KEYS.dayPrefix}${get().todayKey}`, tl);
  },

  patchTodayLog: async (patch) => {
    const cur = get().todayLog;
    const next = typeof patch === 'function' ? patch(cur) : { ...cur, ...patch };
    set({ todayLog: next });
    await storageSet(`${KEYS.dayPrefix}${get().todayKey}`, next);
  },

  setMeasurements: async (m) => {
    set({ measurements: m });
    await storageSet(KEYS.measurements, m);
  },

  setRecipes: async (r) => {
    set({ recipes: r });
    await storageSet(KEYS.recipes, r);
  },

  setShopping: async (s) => {
    set({ shopping: s });
    await storageSet(KEYS.shopping, s);
  },

  setMotivTracks: async (m) => {
    set({ motivTracks: m });
    await storageSet(KEYS.motivTracks, m);
  },

  pushWorkoutHistory: async (s) => {
    const next = [...get().workoutHistory, s];
    set({ workoutHistory: next });
    await storageSet(KEYS.workoutHistory, next);
  },

  setTip: async (t, persist = true) => {
    set({ tip: t });
    if (persist) await storageSet(`${KEYS.tipPrefix}${get().todayKey}`, t);
  },

  /**
   * Bascule de jour : appelé par useToday quand minuit est franchi.
   * - Le todayLog précédent reste persisté sous son ancienne clé.
   * - On charge la nouvelle clé (vide si premier passage).
   * - Le tip est régénéré (random + persistance pour le nouveau jour).
   */
  rolloverDay: async (newKey) => {
    const todayLog = await storageGet<TodayLog>(`${KEYS.dayPrefix}${newKey}`, {});
    const savedTip = await storageGet<string | null>(`${KEYS.tipPrefix}${newKey}`, null);
    let tip = savedTip ?? '';
    if (!tip) {
      tip = pickRandomTip();
      void storageSet(`${KEYS.tipPrefix}${newKey}`, tip);
    }
    set({ todayKey: newKey, todayLog, tip });
  },

  /**
   * Reset complet : purge TOUTES les clés apollo:* (incluant day/tip/coach)
   * et remet le store à l'état d'onboarding.
   */
  reset: async () => {
    await storageClearApollo();
    set({
      ...initial,
      hydrated: true,
      todayKey: todayKey(),
      tip: pickRandomTip(),
    });
  },
}));
